import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useNavigate } from "react-router-dom";
import { StyledLink } from "../../components/CustomMUI";
import { useAuth } from "../../context/authContext";
import { INTENT_PARAM } from "../../hooks/useUrlIntent";
import { getContentOptions } from "../../utils/utils";
import AchievementDetails from "./AchievementDetails";
import ShareProfileDialog from "./ShareProfileDialog";

// Row `icon` keys → icons; content types reuse the app's own type icons.
// Shared with the unlock toast (AwardCelebrations).
const ICON_CONTENT_TYPES = { movie: "movie", tv: "tvshow", book: "book" };
export const getAchievementIcon = (key) =>
  key === "checklist" ? (
    <ChecklistRoundedIcon />
  ) : (
    getContentOptions()[ICON_CONTENT_TYPES[key]]?.icon
  );

// Backend route keys → paths (the backend can't know the viewer's own paths).
// An action's contentType opens that type's tab of the page; its intent asks
// the page to open something on arrival (see useUrlIntent).
const resolveHref = ({ href, contentType, intent }, username) => {
  const path =
    {
      settings_profile: "/settings/profile",
      own_profile: `/u/${username}/profile`,
      own_collections: `/curate/u/${username}`,
      import: "/import",
    }[href] ?? href;
  const params = new URLSearchParams();
  const slug = contentType && getContentOptions()[contentType]?.slug;
  if (slug) params.set("type", slug);
  if (intent) params.set(INTENT_PARAM, intent);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

// Desktop: one line, with fixed title and action columns so every row's bar
// starts and ends at the same place. Mobile: icon, title and action, then the
// bar under the title.
const ROW_GRID = {
  display: "grid",
  alignItems: "center",
  columnGap: 1.5,
  rowGap: 0.75,
  // Flush with the section title: the rows sit on the page, not in a panel
  px: 0,
  py: { xs: 1.25, sm: 1 },
  minHeight: { sm: 56 },
  gridTemplateColumns: {
    // Equal title and action columns, so long ones wrap at the same place
    // on every row
    xs: "40px minmax(0, 1fr) minmax(0, 1fr)",
    sm: "40px 220px minmax(120px, 1fr) 180px",
  },
  gridTemplateAreas: {
    xs: `"icon title action" ". bar bar"`,
    sm: `"icon title bar action"`,
  },
};

// Every action is right-aligned text — a link or a muted note — so they line
// up across rows, wrapping within the fixed action column. Links keep the text
// colour, with the underline as the affordance (StyledLink).
const ACTION_SX = { textAlign: "right" };

/** Earned in the last week — a quiet echo of the unlock toast. MUI's dot Badge size, as on the what's-new menu dot. */
const NewDot = ({ display }) => (
  <Box
    role="img"
    aria-label="New"
    sx={{
      display,
      width: 8,
      height: 8,
      borderRadius: "50%",
      bgcolor: "tertiary.main",
      flexShrink: 0,
    }}
  />
);

/** Hints that the title opens the row's details */
const DetailsChevron = ({ display }) => (
  <ChevronRightRoundedIcon
    sx={{ display, fontSize: 18, color: "text.disabled", ml: -0.25 }}
  />
);

/**
 * One dashboard achievement, rendered purely from the backend's generic row
 * DTO (see services/achievements/rows.py) so new kinds need no new component:
 * icon · title · progress bar · action. The icon, title and bar open the
 * row's details (AchievementDetails); the action stays separate, so the row
 * isn't a link as a whole.
 */
export default function AchievementRow({ row, onSkip, onSeen }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dialog, setDialog] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const titleRef = useRef(null);

  // Reports the row once it's properly on screen (DashboardProgress clears
  // NEW dots that way). Also when it becomes reportable while already in
  // view — e.g. a badge earned while you're on the dashboard.
  const rowRef = useRef(null);
  const isInView = useInView(rowRef, { once: true, amount: 0.6 });
  const onSeenRef = useRef(onSeen);
  onSeenRef.current = onSeen;
  const isReportable = !!onSeen;
  useEffect(() => {
    if (isInView && isReportable) onSeenRef.current();
  }, [isInView, isReportable]);

  const { start = 0, current, target, label: progressLabel } = row.progress;
  // Clamped: an un-finish can leave the count below a permanently held level
  const barPercent = Math.min(
    100,
    Math.max(0, ((current - start) / (target - start || 1)) * 100),
  );
  const ariaLabel = progressLabel
    ? `${row.title}: ${current} of ${target} towards ${progressLabel.toLowerCase()}`
    : `${row.title}: ${current} of ${target}`;

  const handleNavigate = () =>
    row.action.dialog
      ? setDialog(row.action.dialog)
      : navigate(resolveHref(row.action, user?.username));

  const hasDetails = !!row.details;
  const openDetails = () => hasDetails && setIsDetailsOpen(true);
  // Icon and bar are pointer shortcuts; the title is the keyboard-reachable control
  const detailsTriggerSx = hasDetails ? { cursor: "pointer" } : undefined;

  const isTogoWithTarget =
    row.action?.type === "togo" &&
    row.action.remaining != null &&
    !!progressLabel;

  const renderAction = () => {
    if (row.isMaxed || !row.action) {
      return (
        <Typography variant="body2" color="text.secondary" sx={ACTION_SX}>
          Max level
        </Typography>
      );
    }
    switch (row.action.type) {
      case "task":
      case "link":
        return (
          <StyledLink
            component="button"
            variant="body2"
            onClick={handleNavigate}
            sx={ACTION_SX}
          >
            {row.action.label}
          </StyledLink>
        );
      // "togo", and any future type this client doesn't know yet
      default:
        return (
          <Typography variant="body2" color="text.secondary" sx={ACTION_SX}>
            {/* Desktop names the target inline ("33 to Level 4"); mobile
                keeps "33 to go" with the target on its own line beneath */}
            {isTogoWithTarget ? (
              <>
                <Box
                  component="span"
                  sx={{ display: { xs: "inline", sm: "none" } }}
                >
                  {row.action.label}
                </Box>
                <Box
                  component="span"
                  sx={{ display: { xs: "none", sm: "inline" } }}
                >
                  {`${row.action.remaining} to ${progressLabel}`}
                </Box>
              </>
            ) : (
              row.action.label
            )}
          </Typography>
        );
    }
  };

  return (
    <Box ref={rowRef} sx={ROW_GRID}>
      <Box
        onClick={openDetails}
        sx={{
          gridArea: "icon",
          display: "flex",
          justifyContent: "center",
          color: "text.secondary",
          ...detailsTriggerSx,
        }}
      >
        {getAchievementIcon(row.icon)}
      </Box>

      {/* Level beside the title on desktop, beneath it on mobile. The new
          dot ends the first line: after the title on mobile, after the level
          on desktop. */}
      <Stack
        ref={titleRef}
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        columnGap={0.75}
        {...(hasDetails && {
          role: "button",
          tabIndex: 0,
          "aria-haspopup": "dialog",
          "aria-expanded": isDetailsOpen,
          "aria-label": `How ${row.title} works`,
          onClick: openDetails,
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openDetails();
            }
          },
        })}
        sx={{ gridArea: "title", minWidth: 0, ...detailsTriggerSx }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body2" fontWeight="bold">
            {row.title}
          </Typography>
          {row.isNew && <NewDot display={{ xs: "block", sm: "none" }} />}
          {hasDetails && (
            <DetailsChevron display={{ xs: "block", sm: "none" }} />
          )}
        </Stack>
        {/* Badges show their level from 0; checklists have none */}
        {row.kind === "badge" && (
          <Typography variant="body2" color="text.secondary" noWrap>
            Level {row.level}
          </Typography>
        )}
        {row.isNew && <NewDot display={{ xs: "none", sm: "block" }} />}
        {hasDetails && <DetailsChevron display={{ xs: "none", sm: "block" }} />}
      </Stack>

      <LinearProgress
        variant="determinate"
        color="primary"
        // Between levels: empty at the held level's threshold, full at the next
        value={barPercent}
        // Real values rather than MUI's 0–100 percentage; with no count on
        // screen, the label is the only place the numbers appear
        aria-valuenow={current}
        aria-valuemin={start}
        aria-valuemax={target}
        aria-label={ariaLabel}
        onClick={openDetails}
        // Unfilled track in the secondary colour, not MUI's faded primary
        sx={{
          ...detailsTriggerSx,
          gridArea: "bar",
          height: 6,
          borderRadius: 3,
          bgcolor: "secondary.main",
        }}
      />

      <Stack alignItems="flex-end" sx={{ gridArea: "action" }}>
        {renderAction()}
        {/* Mobile only: the bar's target label, matching the held level opposite */}
        {progressLabel && (
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ display: { xs: "block", sm: "none" } }}
          >
            {progressLabel}
          </Typography>
        )}
        {row.canSkip && (
          <StyledLink
            component="button"
            variant="caption"
            onClick={() => onSkip(row.action.stepKey)}
            sx={{ ...ACTION_SX, color: "text.disabled" }}
          >
            Skip
          </StyledLink>
        )}
      </Stack>

      {hasDetails && (
        <AchievementDetails
          row={row}
          anchorEl={titleRef.current}
          open={isDetailsOpen}
          onClose={() => setIsDetailsOpen(false)}
          onAction={() => {
            setIsDetailsOpen(false);
            handleNavigate();
          }}
          onSkip={() => onSkip(row.action.stepKey)}
        />
      )}

      <ShareProfileDialog
        open={dialog === "share_profile"}
        onClose={() => setDialog(null)}
      />
    </Box>
  );
}
